// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { LightBorderButton } from './LightBorderButton';
import {
  ActionButton,
  Callout,
  ICalloutContentStyles,
  IPersonaCoinStyles,
  Persona,
  PersonaSize,
  Stack
} from '@fluentui/react';
import { useState } from 'react';
import { clearLocalStorage } from 'utils/localStorage';
import { CallIdHistoryModal } from './CallIdHistory';
import { useSignIn } from 'hooks/useUser';

export const SignInOutButton = (props: { resetToHome: () => void }): JSX.Element => {
  const { signIn, signedInState } = useSignIn();
  if (signedInState === 'NotSignedIn') {
    return <LightBorderButton text="Sign In" iconProps={{ iconName: 'SignIn' }} onClick={signIn} />;
  }

  return <LoggedInCoin resetToHome={props.resetToHome} />;
};

const LoggedInCoin = (props: { resetToHome: () => void }): JSX.Element => {
  const { signOut, user } = useSignIn();

  const buttonId = 'login-persona';
  const calloutLabelId = 'login-persona-callout-label';
  const [showDetailsFlyout, setShowDetailsFlyout] = useState(false);
  const [showCallhistoryModal, setShowCallHistoryModal] = useState(false);

  return (
    <>
      <Persona
        coinProps={{ styles: personaStyles, onClick: () => setShowDetailsFlyout(!showDetailsFlyout) }}
        hidePersonaDetails
        text={user?.name}
        coinSize={32}
        showOverflowTooltip={false}
        id={buttonId}
      />
      {showDetailsFlyout && (
        <Callout
          ariaLabelledBy={calloutLabelId}
          role="dialog"
          onDismiss={() => setShowDetailsFlyout(false)}
          target={`#${buttonId}`}
          isBeakVisible={false}
          setInitialFocus
          styles={calloutStyles}
          gapSpace={4}
        >
          <Stack tokens={{ childrenGap: '0.5rem' }}>
            <Stack.Item>
              <Persona
                text={user?.name}
                secondaryText={user?.email}
                size={PersonaSize.size40}
                hidePersonaDetails={false}
              />
            </Stack.Item>
            <Stack.Item>
              <ActionButton text="Call Id History" onClick={() => setShowCallHistoryModal(true)} />
            </Stack.Item>
            <Stack.Item>
              <ActionButton text="Clear my preferences" onClick={clearLocalStorage} />
            </Stack.Item>
            <Stack.Item>
              <ActionButton
                onClick={async () => {
                  await signOut();
                  setShowDetailsFlyout(!showDetailsFlyout);
                  props.resetToHome();
                }}
                text="Sign Out"
              />
            </Stack.Item>
          </Stack>
        </Callout>
      )}
      {showCallhistoryModal && (
        <CallIdHistoryModal isOpen={showCallhistoryModal} onDismiss={() => setShowCallHistoryModal(false)} />
      )}
    </>
  );
};

const personaStyles: Partial<IPersonaCoinStyles> = {
  initials: {
    cursor: 'pointer',
    userSelect: 'none'
  }
};

const calloutStyles: Partial<ICalloutContentStyles> = {
  calloutMain: {
    padding: '1rem 1rem 0.25rem 1rem',
    minWidth: '7rem'
  }
};
